import Logger from "./Logger.ts";
import { isDefined, isNull, isUndefined } from "./utils.ts";

type Callback = (status: number, response: unknown) => void;

export default class Request {
  private window: Window & typeof globalThis;

  constructor(
    window: Window & typeof globalThis = {} as Window & typeof globalThis
  ) {
    this.window = window;
  }

  post = (
    url: string,
    data: Record<string, unknown> = {},
    callback: Callback,
    headers: Record<string, string> = {},
    sync: boolean = true
  ): void => {
    const { XMLHttpRequest } = this.window;

    Logger.debug("Request : POST request with data");

    if (XMLHttpRequest) {
      this._makeXmlHttpRequest(url, data, callback, headers, sync);
    } else {
      Logger.error("Request : Unknown browser");
      callback(403, "Unknown");
    }

    Logger.debug("Request : Sent request to server");
  };

  get = (url: string, callback: Callback): void => {
    const { fetch, XMLHttpRequest } = this.window;

    if (fetch) {
      fetch(url)
        .then((res: Response) => {
          try {
            if (res.ok) return res.json();
          } catch (e) {
            Logger.debug(`Request : Error while parsing the response ${e}`);
          }
          return null;
        })
        .then((json: unknown) => {
          if (isDefined(json)) {
            callback(200, json);
          } else {
            Logger.error(
              `Request : API request failed. ${JSON.stringify(json)}`
            );
            callback(500, {});
          }
        })
        .catch((err: unknown) => {
          Logger.error(`Request : API request failed. ${err}`);
          callback(500, err);
        });
    } else if (XMLHttpRequest) {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.onreadystatechange = function () {
        if (xhr.readyState === 4) {
          callback(xhr.status, xhr.responseText);
        }
      };
      xhr.send();
    } else {
      Logger.error("Request : Unknown browser");
      callback(403, "Unknown");
    }

    Logger.debug("Request : sent request to server");
  };

  private _makeXmlHttpRequest = (
    url: string,
    data: Record<string, unknown>,
    callback: Callback,
    headers: Record<string, string> | boolean,
    sync?: boolean
  ): void => {
    const xhr = new XMLHttpRequest();

    xhr.onerror = (err: ProgressEvent): void => {
      Logger.error(`Request : API request failed. ${err}`);
      callback(500, null);
    };

    // When called recursively on sync failure, `headers` is repurposed as the
    // async flag (boolean). Normalise both cases here.
    const resolvedHeaders: Record<string, string> =
      typeof headers === "boolean" ? {} : headers ?? {};
    const resolvedSync: boolean =
      typeof headers === "boolean" ? headers : sync ?? true;

    xhr.open("POST", url, resolvedSync);
    xhr.setRequestHeader("Content-Type", "application/json; charset=UTF-8");

    Object.keys(resolvedHeaders).forEach((key) => {
      xhr.setRequestHeader(key, resolvedHeaders[key]);
    });

    xhr.onreadystatechange = (): void => {
      if (xhr.readyState === 4) {
        let response: unknown = xhr.responseText;

        // If synchronous XHR is blocked on page dismissal, retry asynchronously.
        const expr = (response as string).match(
          /Failed to execute 'send' on 'XMLHttpRequest'/
        );
        if (
          !isNull(expr) &&
          !isUndefined(expr) &&
          !isNull(expr[0]) &&
          !isUndefined(expr[0]) &&
          expr[0] === "Failed to execute 'send' on 'XMLHttpRequest'"
        ) {
          this._makeXmlHttpRequest(url, data, callback, true);
          return;
        }

        try {
          response = JSON.parse(response as string);
        } catch (e) {
          Logger.debug(`Request : Error while parsing the response ${e}`);
        }

        callback(xhr.status, response);
      }
    };

    try {
      xhr.send(JSON.stringify(data));
    } catch (e: unknown) {
      Logger.error(`Request : API request failed. ${e}`);
      callback(500, e);
    }
  };
}
